import {ExpectedInboundPackaging, Packaging, ResponseErrorV2} from '../../shared/CommonInterfaces';

export type InboundType = 'floor_loaded' | 'palletized' | 'parcel_receive' | 'unknown';

export interface PatchInboundRequest {
  arrivalDatetime?: string; // in ISO format YYYY-MM-DDTHH:mm:ss.sss
  actualInboundType?: InboundType;
}

export interface Inbound {
  id: number;
  reservation: InboundReservation;
  purchaseOrders: string[];
  expectedArrivalDate: string; // YYYY-MM-DD
  actualArrivalTime?: Date; // in UTC format
  receivingWindow?: {
    startAt: string; // in format 'hh:mm a'
    endAt: string; // in format 'hh:mm a'
  };
  expectedInboundPackaging: string; //floor_loaded, palletized, or unknown
  actualInboundPackaging?: string; //floor_loaded, palletized, or unknown
  state: string;
  trailerNumber?: string;
  sealNumber?: string;
  instructions?: string;
  providedProperties?: Map<string, string>;
  capturableProperties?: Map<string, InboundCapturableProperty>;
  completedBy?: string; // name of user that completed the inbound
  requireReferenceId: boolean;
  vendor?: string;
  customerUuid: string;
  lockVersion?: number;
  moveDate: string; // in format YYYY-MM-DD
  hasBillOfLading?: boolean;
}

export interface InboundReservation {
  id: number;
  scopeId: number;
  shipperId: number;
  shipperName: string;
  warehouseId: number;
}

export interface InboundCapturableProperty {
  type: string;
  instructions: string;
  required: boolean;
  value?: any;
  displayLabel?: string;
  defaultValue?: any;
}

export interface Property {
  unitsPerParent: number;
}

export interface ItemQuantity {
  packaging: Packaging;
  amount: number;
  isDamaged: boolean;
  packingListId?: number;
  uoms?: InboundLpnItemUoms;
}

export interface InventoryProperties {
  each?: Property;
  carton?: Property;
}

export interface InventoryInfo {
  inventoryId: number;
  sku: string;
  description: string;
  properties: InventoryProperties;
}

export interface SkuContent {
  item: InventoryInfo;
  purchaseOrder: string;
  expectedQuantity?: ItemQuantity;
  receivedQuantities: ItemQuantity[];
}

export interface SkuContents {
  skus: SkuContent[];
}

export interface GetInboundSkuContentsByIdResponse {
  data: SkuContents;
  errors: ResponseErrorV2[];
}

export interface GetInboundsBaseFilters {
  'warehouse-id': number;
  'inbound-id[]'?: number[];
  'reservation-id[]'?: number[];
  'state[]'?: string[];
  'inventory-id[]'?: number[];
  'lpn-type[]'?: string[];
  'continuation-token'?: string;
  'page-size'?: number;
}

export interface GetInboundsFilters extends GetInboundsBaseFilters {
  'sku[]'?: string[];
  'trailer-number'?: string;
  // applying both move start and end will do a time range search
  'move-date-start'?: string; // must be format YYYY-mm-dd
  'move-date-end'?: string; // must be format YYYY-mm-dd
  'seal-number'?: string;
  'vendor'?: string;
  'purchase-order[]'?: string[];
  'search-string'?: string; // for generic search
}

// Request filters for pagination
export interface GetInboundLpnContentsFilters {
  continuationToken?: string;
  pageSize?: number;
}

// Response Interfaces
interface InboundLpnItemQuantity {
  packaging: string;
  amount: number;
}

interface InboundLpnItemProperties {
  each: {unitsPerParent: number};
  carton: {unitsPerParent: number};
}

export interface InboundLpnItemItd {
  id: number;
  lotCode: string;
  expirationDate: string;
}

interface InboundLpnItemUoms {
  eachesPerInnerPack: number;
  eachesPerOuterCase: number;
}

export interface InboundLpnItem {
  id: number;
  sku: string;
  description: string;
  properties: InboundLpnItemProperties;
  itd: InboundLpnItemItd;
  expectedQuantity?: InboundLpnItemQuantity;
  receivedQuantities?: InboundLpnItemQuantity;
  uoms?: InboundLpnItemUoms;
}

export interface InboundLpn {
  id: number;
  barcode: string;
  purchaseOrder: string;
  isDamaged: boolean;
  receivedAt?: string;
  items: InboundLpnItem[];
}

export interface LpnContents {
  continuationToken: string | null;
  totalLpns: number;
  lpns: InboundLpn[];
}

export interface GetInboundLpnContentsByIdResponse {
  data: LpnContents;
  errors: ResponseErrorV2[];
}

export const FLOOR_LOADED_TYPE = 1;
export const PALLETIZED_TYPE = 2;
export const PARCEL_RECEIVE_TYPE = 3;
export const UNKNOWN_TYPE = 4;

export const InboundPackagingMap = new Map<string, number>([
  [ExpectedInboundPackaging.FloorLoaded, FLOOR_LOADED_TYPE],
  [ExpectedInboundPackaging.Palletized, PALLETIZED_TYPE],
  [ExpectedInboundPackaging.ParcelReceive, PARCEL_RECEIVE_TYPE],
  [ExpectedInboundPackaging.Unknown, UNKNOWN_TYPE]
]);

export enum InboundState {
  New = 'new',
  Confirmed = 'confirmed',
  Altered = 'altered',
  Arrived = 'arrived',
  Receiving = 'receiving',
  Completed = 'completed',
  Cancelled = 'cancelled'
}

// Map states to user-friendly display names, similar to TransitionStateDisplay.
export const InboundStateDisplay: Map<InboundState, string> = new Map([
  [InboundState.New, 'New'],
  [InboundState.Confirmed, 'Confirmed'],
  [InboundState.Altered, 'Altered'],
  [InboundState.Arrived, 'Arrived'],
  [InboundState.Receiving, 'Receiving'],
  [InboundState.Completed, 'Completed'],
  [InboundState.Cancelled, 'Cancelled']
]);

export enum FilterSearchTypes {
  Is = 'Is',
  Contains = 'Contains / Is'
}

export enum InboundFilterKeys {
  PurchaseOrder = 'purchase_order_numbers',
  InboundId = 'inbound_id',
  Sku = 'sku',
  Status = 'status',
  ReservationId = 'reservation_id',
  TrailerNumber = 'trailer_number',
  Vendor = 'vendor',
  MoveDateRange = 'move_date_range',
  MoveDateStart = 'move_date_start',
  MoveDateEnd = 'move_date_end'
}
